/**
 * The Search page's date filter.
 * Contains a graph of the # of works per year,
 * and 2 'thumbs' which can be dragged to set the date range.
 * Also includes buttons for selecting recent years.
 * Issues a request by setting parent's dateRangeYears on mouse up.
 * If on mobile, request is instead issued via an apply button.
 */
import React, {
  forwardRef,
  useEffect,
  useLayoutEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import styled from 'astroturf/react';

import $image_url from 'js/utils/image_url';

const DEFAULT_SLIDER_WIDTH = 226;
const HALF_THUMB_WIDTH = 20;
const THUMB_OVERLAP_THRESHOLD = 34;
const THUMB_MINIMUM = 1;

const Container = styled.div<{ isMobile?: boolean }>`
  display: flex;
  flex-direction: column;
  margin-bottom: 34px;

  &.isMobile {
    margin-bottom: 0;
  }
`;

const YearSelector = styled.div`
  position: relative;
  flex-shrink: 0;
  height: 90px;
  margin: 0 20px 0 20px;
`;

const YearBar = styled.div<{ isSingleDisabled?: boolean }>`
  width: 2px;
  background-color: #094ec3;
  position: absolute;
  bottom: 0;

  &.isSingleDisabled {
    left: 50%;
    transform: translate(-50%, 0);
    height: 100%;
    background-color: #c4c4c4;
  }
`;

const HorizontalBar = styled.div<{ isDisabled?: boolean }>`
  width: 100%;
  height: 2px;
  background-color: rgba(9, 78, 195, 0.3);

  &.isDisabled {
    background-color: #c4c4c4;
    user-select: none;
  }
`;

const SliderThumb = styled.div<{ isSingleDisabled?: boolean }>`
  position: absolute;
  bottom: -38px;
  height: 38px;
  width: 40px;
  user-select: none;
  cursor: grab;

  &.isSingleDisabled {
    left: calc(50% - ${HALF_THUMB_WIDTH}px);
  }
`;

const YearSliderNumber = styled.div`
  color: #fff;
  position: absolute;
  top: 13px;
  left: 0;
  width: 100%;
  text-align: center;
  pointer-events: none;
  font-size: 11px;
`;

const OverlapSliderNumber = styled.div`
  color: #fff;
  position: absolute;
  top: 13px;
  text-align: center;
  font-size: 11px;
  z-index: 1;
  pointer-events: none;
  justify-content: center;
`;

const SliderBarContainer = styled.div`
  width: 100%;
  height: 2px;
  position: relative;
  overflow: hidden;
  margin-top: -2px;
`;

const SliderBar = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  border: 1px solid #094ec3;
  height: 0px;
`;

const Slider = styled('div').attrs((props) => props)`
  position: relative;
  margin: 0 20px 0 20px;
  margin-bottom: 50px;
`;

const SliderImage = styled.img`
  pointer-events: none;
`;

const ButtonsWrapper = styled.div`
  height: 29px;
  margin-bottom: 19px;
`;

const YearSelectButton = styled.button<{ isDisabled?: boolean }>`
  background: transparent;
  font-family: Roboto;
  font-size: 13px;
  font-style: normal;
  font-weight: 500;
  line-height: 13px;
  letter-spacing: 0.01em;
  text-align: center;
  color: #094ec3;
  width: 50%;
  height: 100%;
  border: none;
  padding: 0;
  margin: 0;
  border: 1px solid #094ec3;

  &:active {
    background-color: #f0f1f9;
  }

  &.isDisabled {
    border: 1px solid #c4c4c4;
    color: #c4c4c4;
    user-select: none;
  }
`;

const LeftYearSelectButton = styled(YearSelectButton)`
  border-radius: 4px 0 0 4px;
`;

const RightYearSelectButton = styled(YearSelectButton)`
  border-radius: 0 4px 4px 0;
  border-left: none;
`;

const ApplyButton = styled.button<{ isDisabled?: boolean }>`
  width: 266px;
  height: 35px;
  background: #094ec3;
  border: 1px solid #094ec3;
  border-radius: 4px;
  padding: 6px 0 6px 0;
  text-transform: uppercase;
  font-size: 16px;
  font-weight: 700;
  font-family: Roboto;
  line-height: 21px;
  letter-spacing: 0.02em;
  color: #ffffff;

  &.isDisabled {
    background: #c4c4c4;
    border: 1px solid #c4c4c4;
    user-select: none;
  }
`;

const getThumbLeft = (left: number, sliderWidth: number): string =>
  `calc(${(100 * left) / sliderWidth}% - ${HALF_THUMB_WIDTH}px)`;
const getOverlapLeft = (left: number, sliderWidth: number): string =>
  `calc(${(100 * left) / sliderWidth}%)`;
const getSliderBarLeft = (left: number, sliderWidth: number): string =>
  `calc(${(100 * left) / sliderWidth}% + ${HALF_THUMB_WIDTH}px - 3px)`;
const getScale = (
  minYear: number,
  maxYear: number,
  sliderWidth: number,
): number => (sliderWidth - 2) / (maxYear - minYear);
const getYearBarLeftPos = (
  year: number,
  minYear: number,
  scale: number,
): number => (year - minYear) * scale;
const getYearForX = (x: number, minYear: number, scale: number): number =>
  Math.floor(x / scale + minYear);
const getShortFormYear = (year: number): string => {
  const shortYear = year % 100;
  if (shortYear < 10) return `'0${shortYear}`;
  return `'${shortYear}`;
};

let moveEventListeners: ((e: MouseEvent) => void)[] = [];
let touchEventListeners: ((e: TouchEvent) => void)[] = [];

type Props = {
  // Allow external styling
  className?: string;
  // Allow initial state to be passed in
  initialYears?: [] | [number | null, number | null];
  // A mapping of year to the # of works from the year / max(works in a year).
  // The value should always be between 0 and 1.
  // Any year not given is assumed to be 0.
  // ex) { 1977: 0.1, 1979: 0.7, 1980: 0.45, ... }
  worksPerYear: { [worksPercent: number]: number };
  // sets the years used for the fetch request, ex. [1970, 2020]
  setDateRangeYears: (years: Array<number>) => void;
  isMobile: boolean;
  // When true, at the min year show "< {minYear}", indicating the range is unbounded
  isUnboundedMinRange?: boolean;
};

const ActiveState = forwardRef(
  (
    {
      className,
      initialYears = [],
      worksPerYear,
      setDateRangeYears,
      isMobile,
      isUnboundedMinRange = false,
    }: Props,
    ref: React.Ref<{ resetYears(): void }>,
  ) => {
    const years = Object.keys(worksPerYear).map((year) => Number(year));
    const minYear = Math.min(...years);
    const maxYear = Math.max(...years);

    const [fetching, setFetching] = useState(false);
    const [sliderWidth, setSliderWidth] = useState(DEFAULT_SLIDER_WIDTH);
    // These states are not used for the fetch, only for the slider's thumbs
    // because they change so rapidly.
    // Passing in `initialYears` here is only half the work,
    // the other half is the `useEffect` below that sets the thumbs' left style
    const [thumb1Year, setThumb1Year] = useState(initialYears[0] ?? minYear);
    const [thumb2Year, setThumb2Year] = useState(initialYears[1] ?? maxYear);
    // showOverlapThumb is kept in state to immedietely force a rerender when set,
    // despite that showOverlapThumb is a function of the year states.
    const [showOverlapThumb, setShowOverlapThumb] = useState(false);

    // This is a bit wonky - the DateFilter store its years
    // internally in its own state and sends them up
    // to its parent via `setDateRangeYears`, called when 'fetching' is set.
    // But, the parent Search component needs to be able to
    // reset the years selected in DateFilter.
    // Pulling its state up into the parent isn't viable for
    // DateFilter due to performance reasons,
    // so we expose this imperative function that the parent
    // can call when it needs to reset the years.
    useImperativeHandle(ref, () => ({
      resetYears: () => {
        setThumb1Year(minYear);
        setThumb2Year(maxYear);
        // Have to call these things manually, otherwise
        // the year updates on the thumb but its position
        // doesn't change
        if (thumbRef1.current) {
          thumbRef1.current.style.left = getThumbLeft(
            getYearBarLeftPos(minYear, minYear, scale),
            sliderWidth,
          );
        }
        if (thumbRef2.current) {
          thumbRef2.current.style.left = getThumbLeft(
            getYearBarLeftPos(maxYear, minYear, scale),
            sliderWidth,
          );
        }
        updateSlider();
      },
    }));

    const scale = getScale(minYear, maxYear, sliderWidth);

    const yearSelectorRef = useRef<HTMLDivElement>(null);
    const sliderRef = useRef<HTMLDivElement>(null);
    const thumbRef1 = useRef<HTMLDivElement>(null);
    const thumbRef2 = useRef<HTMLDivElement>(null);
    const sliderBarRef = useRef<HTMLDivElement>(null);
    const overlapThumbRef = useRef<HTMLDivElement>(null);
    const diff = useRef<number>();

    const handleMouseMove = (thumbRef: React.RefObject<HTMLDivElement>) => {
      return (event: MouseEvent) => {
        const newX =
          event.clientX -
          (diff.current || 0) -
          (sliderRef.current?.getBoundingClientRect().left || 0);

        handleMove(thumbRef, newX);
      };
    };

    const handleTouchMove = (thumbRef: React.RefObject<HTMLDivElement>) => {
      return (event: TouchEvent) => {
        const newX =
          event.touches[0].pageX -
          (diff.current || 0) -
          (sliderRef.current?.getBoundingClientRect().left || 0);

        handleMove(thumbRef, newX);
      };
    };

    const handleMove = (
      thumbRef: React.RefObject<HTMLDivElement>,
      x: number,
    ) => {
      let newX = x;
      if (x < THUMB_MINIMUM) {
        newX = THUMB_MINIMUM;
      }
      if (x > sliderWidth) {
        newX = sliderWidth;
      }

      if (thumbRef.current) {
        thumbRef.current.style.left = getThumbLeft(newX, sliderWidth);
      }

      if (thumbRef === thumbRef1) {
        setThumb1Year(getYearForX(newX, minYear, scale));
      } else {
        setThumb2Year(getYearForX(newX, minYear, scale));
      }

      updateSlider();
    };

    const handleMouseUp = () => {
      if (!isMobile) {
        setFetching(true);
      }

      document.removeEventListener('mouseup', handleMouseUp);
      moveEventListeners.forEach((listener) => {
        document.removeEventListener('mousemove', listener);
      });
      moveEventListeners = [];
    };

    const handleTouchEnd = () => {
      if (!isMobile) {
        setFetching(true);
      }

      document.removeEventListener('touchend', handleTouchEnd);
      touchEventListeners.forEach((listener) => {
        document.removeEventListener('touchmove', listener);
      });
      touchEventListeners = [];
    };

    const handleMouseDown = (
      event: React.MouseEvent<HTMLDivElement, MouseEvent>,
      thumbRef: React.RefObject<HTMLDivElement>,
    ) => {
      diff.current =
        event.clientX -
        (thumbRef.current?.getBoundingClientRect().left || 0) -
        HALF_THUMB_WIDTH;

      const mouseMove = handleMouseMove(thumbRef);
      moveEventListeners.push(mouseMove);
      document.addEventListener('mousemove', mouseMove);

      document.addEventListener('mouseup', handleMouseUp);
    };

    const handleTouchStart = (
      event: React.TouchEvent<HTMLDivElement>,
      thumbRef: React.RefObject<HTMLDivElement>,
    ) => {
      diff.current =
        event.touches[0].pageX -
        (thumbRef.current?.getBoundingClientRect().left || 0) -
        HALF_THUMB_WIDTH;

      const touchMove = handleTouchMove(thumbRef);
      touchEventListeners.push(touchMove);
      document.addEventListener('touchmove', touchMove);

      document.addEventListener('touchend', handleTouchEnd);
    };

    const selectPastYears = (numYears: number): void => {
      const minSelectedYear = maxYear - numYears;
      if (thumbRef1.current) {
        thumbRef1.current.style.left = getThumbLeft(
          getYearBarLeftPos(minSelectedYear, minYear, scale),
          sliderWidth,
        );
      }
      setThumb1Year(minSelectedYear);

      if (thumbRef2.current) {
        thumbRef2.current.style.left = getThumbLeft(
          getYearBarLeftPos(maxYear, minYear, scale),
          sliderWidth,
        );
      }
      setThumb2Year(maxYear);

      if (!isMobile) {
        setFetching(true);
      }
      updateSlider();
    };

    // Updates the SliderBar's position as the thumbs move.
    // Also detects when the thumbs overlap enough such that years need to be shortened.
    // Needs to be very responsive to avoid jittering, therefore not an effect.
    const updateSlider = () => {
      const thumb1Left = thumbRef1.current?.offsetLeft || 0;
      const thumb2Left = thumbRef2.current?.offsetLeft || 0;
      const thumbDiff = Math.abs(thumb1Left - thumb2Left);
      const newLeft = thumb1Left > thumb2Left ? thumb2Left : thumb1Left;

      if (sliderBarRef.current) {
        sliderBarRef.current.style.left = getSliderBarLeft(
          newLeft,
          sliderWidth,
        );
        sliderBarRef.current.style.width = `${thumbDiff + 5}px`;
      }

      if (thumbDiff < THUMB_OVERLAP_THRESHOLD) {
        if (overlapThumbRef.current) {
          overlapThumbRef.current.style.left = getOverlapLeft(
            newLeft,
            sliderWidth,
          );
          overlapThumbRef.current.style.width = `${thumbDiff + 40}px`;
        }
        // force rerender to avoid jittering
        setShowOverlapThumb(true);
      } else {
        setShowOverlapThumb(false);
      }
    };

    useLayoutEffect(() => {
      if (!isMobile) {
        setSliderWidth(yearSelectorRef.current?.offsetWidth || 0);
        const handler = () => {
          setSliderWidth(yearSelectorRef.current?.offsetWidth || 0);
        };

        window.addEventListener('resize', handler);
        return () => {
          window.removeEventListener('resize', handler);
        };
      }
      // NATIONAL PARKS: Please remove this disable and try to fix it if you're here
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Quick and dirty solution to get DateFilter working on the recommendations page too.
    // We only allow this effect to be run twice.
    // Any less and initial thumb positions will be incorrect due to out of date updateSlider.
    // If run any time updateSlider changes, it will never allow the thumbs to move.
    const updatedInitialYears = useRef(0);
    // Update thumbs and slider (bottom blue bar) positions
    // if initialYears are provided (these come from URL state)
    useLayoutEffect(() => {
      if (updatedInitialYears.current > 1) return;

      const [start, end] = initialYears;
      if (!start && !end) return;

      if (start) {
        // @ts-ignore
        thumbRef1.current.style.left = getThumbLeft(
          getYearBarLeftPos(start, minYear, scale),
          sliderWidth,
        );
      }

      if (end) {
        // @ts-ignore
        thumbRef2.current.style.left = getThumbLeft(
          getYearBarLeftPos(end, minYear, scale),
          sliderWidth,
        );
      }

      updateSlider();
      updatedInitialYears.current += 1;
      // NATIONAL PARKS: Please remove this disable and try to fix it if you're here
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialYears, updateSlider]);

    const thumb1YearLower = thumb1Year < thumb2Year;
    const highYear = thumb1YearLower ? thumb2Year : thumb1Year;
    const lowYear = thumb1YearLower ? thumb1Year : thumb2Year;

    useEffect(() => {
      if (fetching) {
        setDateRangeYears([lowYear, highYear]);
        setFetching(false);
      }
      // NATIONAL PARKS: Please remove this disable and try to fix it if you're here
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fetching]);

    return (
      <Container
        className={className}
        isMobile={isMobile}
        style={years.length === 1 ? { pointerEvents: 'none' } : undefined}
      >
        {years.length === 1 ? (
          <SimpleSingleYear year={years[0]} isMobile={isMobile} />
        ) : (
          <>
            <YearSelector ref={yearSelectorRef}>
              {years.map((year) => (
                <YearBar
                  key={year}
                  style={{
                    left: getYearBarLeftPos(year, minYear, scale),
                    height: `${worksPerYear[year] * 100}%`,
                    backgroundColor:
                      lowYear <= year && year <= highYear
                        ? '#094EC3'
                        : 'rgba(9, 78, 195, 0.3)',
                  }}
                />
              ))}
            </YearSelector>
            <HorizontalBar />
            <Slider ref={sliderRef}>
              <SliderBarContainer>
                <SliderBar ref={sliderBarRef} />
              </SliderBarContainer>
              <SliderThumb
                ref={thumbRef1}
                onMouseDown={(e) => handleMouseDown(e, thumbRef1)}
                onTouchStart={(e) => handleTouchStart(e, thumbRef1)}
                style={{ left: -HALF_THUMB_WIDTH + 1 }}
              >
                <div style={{ position: 'relative' }}>
                  <SliderImage
                    src={$image_url('search/date-filter-slider.svg')}
                  />
                  {!showOverlapThumb && (
                    <YearSliderNumber>
                      {isUnboundedMinRange && thumb1Year === minYear
                        ? `<${thumb1Year}`
                        : `${thumb1Year}`}
                    </YearSliderNumber>
                  )}
                </div>
              </SliderThumb>
              <OverlapSliderNumber
                ref={overlapThumbRef}
                style={{ display: showOverlapThumb ? 'flex' : 'none' }}
              >
                {thumb1Year > thumb2Year
                  ? `${getShortFormYear(thumb2Year)} - ${getShortFormYear(
                      thumb1Year,
                    )}`
                  : thumb2Year > thumb1Year
                    ? `${getShortFormYear(thumb1Year)} - ${getShortFormYear(
                        thumb2Year,
                      )}`
                    : `${thumb1Year}`}
              </OverlapSliderNumber>
              <SliderThumb
                ref={thumbRef2}
                onMouseDown={(e) => handleMouseDown(e, thumbRef2)}
                onTouchStart={(e) => handleTouchStart(e, thumbRef2)}
                style={{
                  left: getYearBarLeftPos(maxYear, minYear, scale) - 18,
                }}
              >
                <div style={{ position: 'relative' }}>
                  <SliderImage
                    src={$image_url('search/date-filter-slider.svg')}
                  />
                  {!showOverlapThumb && (
                    <YearSliderNumber>
                      {isUnboundedMinRange && thumb2Year === minYear
                        ? `<${thumb2Year}`
                        : `${thumb2Year}`}
                    </YearSliderNumber>
                  )}
                </div>
              </SliderThumb>
            </Slider>
            <ButtonsWrapper>
              <LeftYearSelectButton onClick={() => selectPastYears(1)}>
                Past Year
              </LeftYearSelectButton>
              <RightYearSelectButton onClick={() => selectPastYears(5)}>
                Past 5 Years
              </RightYearSelectButton>
            </ButtonsWrapper>
            {isMobile && (
              <ApplyButton onClick={() => setFetching(true)}>Apply</ApplyButton>
            )}
          </>
        )}
      </Container>
    );
  },
);

const SimpleSingleYear = ({
  year,
  isMobile,
}: {
  year: number;
  isMobile: boolean;
}) => (
  <>
    <YearSelector>
      <YearBar isSingleDisabled />
    </YearSelector>
    <HorizontalBar isDisabled />
    <Slider>
      <SliderThumb isSingleDisabled>
        <div style={{ position: 'relative' }}>
          <SliderImage src={$image_url('search/date-filter-slider-gray.svg')} />
          <YearSliderNumber>{year}</YearSliderNumber>
        </div>
      </SliderThumb>
    </Slider>
    <ButtonsWrapper>
      <LeftYearSelectButton isDisabled>Past Year</LeftYearSelectButton>
      <RightYearSelectButton isDisabled>Past 5 Years</RightYearSelectButton>
    </ButtonsWrapper>
    {isMobile && <ApplyButton isDisabled>Apply</ApplyButton>}
  </>
);

export default ActiveState;
